import { Formik } from "formik";
import React from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { CreateNotification } from "../../../components/notifier";
import { addFolderValidationSchema } from "../../../constants/validationSchema";
import { FileService } from "../../../services/file/file.service";
import { useQueryClient } from "@tanstack/react-query";
import { getEnv } from "../../../AppConfigs";
import { RegExp } from "../../../constants";
import useRenameFolder from "../hooks/documents/use-rename-folder";
import { useSearchParams } from "../../../hook/use-search-params";
import useRenamePublicFolder from "../hooks/documents/use-rename-public-folder";

interface AddFolderModalProps {
  onClose: () => void;
  onSuccess?: (type: "create" | "update") => void | Promise<void>;
  isOpen: boolean;
  oldFolderName: string;
  folderId?: string;
}
const SP_PUBLISH_LIST_ID = getEnv("REACT_APP_PUBLIC_FOLDER_AND_FILE_SP_LIST_ID");

const UpdateFolderModal = (props: AddFolderModalProps) => {
  const isForbidden = useSelector((state: any) => state.main.isForbidden);
  const { onClose, onSuccess, isOpen, oldFolderName, folderId } = props;

  const { searchParams } = useSearchParams();
  const folderIdParam = searchParams.get("folderId");

  const queryClient = useQueryClient();
  const { mutateAsync: renameFolderAsync } = useRenameFolder();
  const { mutateAsync: renamePublicFolderAsync } = useRenamePublicFolder();

  const handleOnSubmit = async (values: any) => {
    const { foldername } = values;
    if (!RegExp.folderNameRegex.test(foldername.trim())) {
      CreateNotification({
        title: "Thông báo",
        body: `Thư mục chứa ký tự không hợp lệ vui lòng thử lại.`,
        icon: "/icons/warning.png",
      });
      return;
    }

    const name = {
      fields: {
        FileLeafRef: foldername,
      },
    };
    try {
      const res = await FileService.updateFolderName(folderId, name);
      CreateNotification({ title: "Thành công", body: `Đổi tên folder thành công.`, icon: "/icons/success.png" });
      await onSuccess?.("update");
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      onClose();

      if (folderId || folderIdParam)
        await renameFolderAsync({
          key: folderIdParam || folderId || "",
          newName: foldername.trim(),
        });

      const publishFolder = await FileService.getFolderByPath(
        res.webUrl ? [...res.webUrl.split("/").slice(6, -1), oldFolderName].join("/") : oldFolderName,
      );

      if (publishFolder) {
        const file = await FileService.getFile(publishFolder.id, SP_PUBLISH_LIST_ID);
        const id = file.listItemId;
        if (id) {
          await FileService.updateFolderName(id, name, SP_PUBLISH_LIST_ID);
          await renamePublicFolderAsync({
            key: publishFolder.id,
            newName: foldername.trim(),
          });
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["publish-folders-new"], type: "all" });
    } catch (error) {
      if (isForbidden) {
        CreateNotification({
          title: "Thông báo",
          body: `Bạn không có quyền để đổi tên thư mục. Vui lòng liên hệ Admin để được hỗ trợ.`,
          icon: "/icons/error.png",
        });
      } else {
        CreateNotification({
          title: "Thông báo",
          body: `Không thể đổi tên thư mục, vui lòng thử lại.`,
          icon: "/icons/error.png",
        });
      }
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered size={"lg"} className="add-folder-modal">
      <Formik
        validationSchema={addFolderValidationSchema}
        onSubmit={handleOnSubmit}
        initialValues={{
          foldername: oldFolderName ? oldFolderName : "",
        }}
      >
        {({ handleSubmit, handleChange, values, errors }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>{"Đổi tên thư mục"}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-4 px-4">
              <Form.Group as={Row} controlId="validationCustom01">
                <Form.Label column sm={2} className="card-label">
                  {"Tên thư mục"}
                </Form.Label>
                <Col sm={10}>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Ex: Quản lý tài liệu"
                    name="foldername"
                    value={values.foldername}
                    onChange={handleChange}
                    isInvalid={!!errors.foldername}
                  />
                  <Form.Control.Feedback type="invalid">{errors.foldername}</Form.Control.Feedback>
                </Col>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={onClose} variant="secondary">
                Hủy
              </Button>
              <Button type="submit" variant="info">
                {"Đổi tên"}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default UpdateFolderModal;
